import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { OlxAdvertService } from '../../../../../services/olx-advert.service';
import { NotificationService } from '../../../../../services/notifications.service';
import { ModalService } from '../../../../../services/modal.service';
import { ChangeOlxPriceComponent } from '../../../../../modals/change-olx-price/change-olx-price.component';
import { DEFAULT_ERROR, ERROR_MESSAGES, activeTranslations } from '../../../../../enum/constants';
import { IError } from '../../../../../interfaces';
import { parseCurrency, parseCurrencyXXX } from '../../../../../helpers';
import { ADVERT_TYPE, IAction, IAdvert } from './chat-advert-info.interface';
import { ChatService } from '../../../../../services/chat.service';
import { catchError, map, of } from 'rxjs';
import { PowerOlxV2Service } from '../../../../../modules/crm/power-olx-v2/services/power-olx-v2.service';
import { OlxAdvertCommand } from '../../../../../modules/crm/power-olx-v2/interfaces/power-olx-v2.interface';

@Component({
  selector: 'chat-advert-info',
  templateUrl: './chat-advert-info.component.html',
  styleUrls: ['./chat-advert-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChatAdvertInfoComponent implements OnChanges, AfterViewInit {
  @Input() advert!: IAdvert;
  @ViewChild('descriptionElement') descriptionElement!: ElementRef;
  protected actions: IAction[] = [
    {
      title: 'change_price',
      icon: 'price_change',
      key: 'change_price',
      action: () => this.changePrice(),
    },
    {
      title: 'deactivate_now',
      icon: 'toggle_off',
      key: 'deactivate_advert',
      action: () => this.deactivateAdvert(),
      is_disabled: true,
    },
    {
      title: 'sync_advert',
      icon: 'sync',
      key: 'sync_advert',
      action: () => this.clickSyncAdvert(),
      is_disabled: true,
    },
  ];
  protected isCollapsed: boolean = true;
  protected isTextClamped: boolean = false;

  constructor(
    private readonly modalService: ModalService,
    private readonly olxAdvertService: OlxAdvertService,
    private readonly powerOlxV2Service: PowerOlxV2Service,
    private readonly notificationService: NotificationService,
    private readonly chatService: ChatService,
    private readonly cdref: ChangeDetectorRef,
  ) {}

  ngAfterViewInit() {
    this.checkTextOverflow();
    this.cdref.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['advert'] && this.advert.type === ADVERT_TYPE.OLX) {
      const syncAction = this.actions.find((action) => action.key === 'sync_advert') || ({} as IAction);
      this.syncAdvert(syncAction);

      const deactivateAction = this.actions.find((action) => action.key === 'deactivate_advert');
      if (deactivateAction) {
        deactivateAction.is_disabled = !['active', 'limited'].includes(this.advert.status);
      }
    }
  }

  private changePrice() {
    const modalData = {
      title: 'change_price',
      component: ChangeOlxPriceComponent,
      translate: true,
      data: {
        price: this.advert.price,
      },
    };
    this.modalService.openModal(modalData).subscribe((price) => {
      if (!price) return;
      this.olxAdvertService.updatePrice(this.chatService.chat.channel_id, `${this.advert.id}`, price).subscribe({
        next: (res) => {
          Object.assign(this.chatService.advert, res);
        },
        error: (res: any) => {
          (res.error?.errors || []).forEach((err: IError) => {
            this.notificationService.showError(
              activeTranslations.notifications_texts.error.error,
              ERROR_MESSAGES[err.code] || DEFAULT_ERROR,
            );
          });
        },
      });
    });
  }

  private deactivateAdvert() {
    const action = this.actions.find((action) => action.key === 'deactivate_advert') || ({} as IAction);
    action.is_disabled = true;
    const lang = activeTranslations.notifications_texts.error;

    this.powerOlxV2Service
      .commandsAdverts(this.chatService.chat.channel_id, [this.advert.id], OlxAdvertCommand.DEACTIVATE)
      .subscribe({
        next: () => {
          this.chatService.advert.status = 'disabled';
          action.is_disabled = true;
        },
        error: (res: any) => {
          action.is_disabled = false;
          (res.error?.errors || []).forEach((err: IError) => {
            this.notificationService.showError(lang.error, ERROR_MESSAGES[err.code] || DEFAULT_ERROR);
          });
        },
      });
  }

  private getSyncAdvert() {
    const last_sync_at: number | null = this.chatService.advert.last_sync_at;

    if (last_sync_at) {
      const currentDate: Date = new Date();
      const lastSyncDate: Date = new Date(last_sync_at);
      const difference: number = (currentDate.getTime() - lastSyncDate.getTime()) / 1000;
      if (difference <= 5) return of(difference);
    }

    return this.olxAdvertService.getLastSync(`${this.advert.id}`).pipe(
      map((res) => {
        const receivedDate: Date = new Date(res);
        this.chatService.advert.last_sync_at = receivedDate.getTime();
        const currentDate: Date = new Date();
        if (isNaN(receivedDate.getTime())) throw new Error();
        return (currentDate.getTime() - receivedDate.getTime()) / 1000;
      }),
      catchError(() => {
        return of(null);
      }),
    );
  }

  private clickSyncAdvert() {
    const action = this.actions.find((action) => action.key === 'sync_advert') || ({} as IAction);
    action.is_disabled = true;
    const lang = activeTranslations.notifications_texts.error;

    this.getSyncAdvert().subscribe((response) => {
      if (!response) {
        this.notificationService.showError(lang.error, lang.synchronization_error);
        action.is_disabled = false;
        return;
      }
      if (response < 5) {
        this.notificationService.showError(lang.error, lang.since_last_sync_error);
        action.is_disabled = false;
        return;
      }
      this.syncAdvert(action);
    });
  }

  private syncAdvert(action: IAction) {
    this.olxAdvertService.syncAdvert(`${this.advert.id}`, this.chatService.chat.channel_id).subscribe((res) => {
      if (res) {
        Object.assign(this.chatService.advert, res);
        this.getSyncAdvert();
        action.is_disabled = false;
      }
    });
  }

  toggleDescription() {
    this.isCollapsed = !this.isCollapsed;
  }

  checkTextOverflow() {
    if (!this.descriptionElement) return;

    const element = this.descriptionElement.nativeElement;
    this.isTextClamped = element.scrollHeight > element.clientHeight;
  }

  protected get statusBadge(): { label: string; color: string } {
    const status = this.advert?.status;
    if (['active'].includes(status)) return { label: 'olx.adverts.active', color: 'green' };
    if (['new', 'disabled', 'unconfirmed'].includes(status)) return { label: 'olx.adverts.pending', color: 'gray' };
    if (['limited', 'unpaid'].includes(status)) return { label: 'olx.adverts.unpaid', color: 'orangered' };
    if (['outdated', 'removed_by_user'].includes(status)) return { label: 'olx.adverts.inactive', color: 'orange' };
    if (['moderated', 'removed_by_moderator', 'blocked'].includes(status))
      return { label: 'olx.adverts.rejected', color: 'red' };
    return { label: 'olx.adverts.pending', color: 'gray' };
  }

  protected readonly parseCurrencyXXX = parseCurrencyXXX;
  protected readonly ADVERT_TYPE = ADVERT_TYPE;
  protected readonly Object = Object;
  protected readonly parseCurrency = parseCurrency;
}
